import { CodeEditor } from '@kodprep/platform-ui/organisms';
import { useQuery } from '@tanstack/react-query';

import { VERDICT_LABEL, formatWhenExact, verdictTone } from './WorkArtifactSheet';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { trainerApi, type ArtifactSide } from '@/lib/api/trainer';
import { cn } from '@/lib/cn';

/**
 * Two submissions the matcher paired, side by side.
 *
 * This is the screen that decides whether the similarity feature is defensible.
 * "These two share distinctive code" is a claim about two named students, and
 * until a trainer can put both solutions next to each other and judge for
 * themselves, it is a claim they are being asked to take on faith and repeat to
 * someone's face. Most of the time reading them settles it in seconds — either
 * it is obviously the same code, or it is two people who solved a simple
 * problem the only sensible way.
 *
 * The submissions shown are the exact ones that were compared, not each
 * student's current best, so the score above them always describes what is on
 * screen.
 */
export function CompareSheet({
  batchId,
  assessmentId,
  pair,
  onClose,
}: {
  batchId: string;
  assessmentId: string;
  pair: { a: string; b: string } | null;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trainer', 'compare', batchId, assessmentId, pair?.a, pair?.b],
    queryFn: async () =>
      (await trainerApi.compareSubmissions(batchId, assessmentId, pair!.a, pair!.b)).data,
    enabled: !!pair,
  });

  return (
    <Sheet open={!!pair} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-5xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="pr-8 text-base">
            {data ? (
              <>
                {data.a.student_name} <span className="text-muted-foreground">vs</span>{' '}
                {data.b.student_name}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {Math.round(data.score * 100)}% alike · {data.shared_count} shared fingerprints ·{' '}
                  {data.item_title}
                </span>
              </>
            ) : (
              'Comparing submissions'
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="grid gap-3 md:grid-cols-2" aria-busy="true">
              <Skeleton className="h-96 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          )}
          {isError && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {/*
                The likeliest cause is a stale pair: similarity was recomputed
                and these two no longer match. Saying so beats a bare error,
                because it tells the trainer not to go and have the conversation.
              */}
              These two submissions could not be loaded. The match may no longer hold — try
              re-running the check.
            </p>
          )}

          {data && (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <Side side={data.a} />
                <Side side={data.b} />
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                Matching ignores names, comments and formatting, so reindented or renamed code still
                scores highly. Shared code can mean copying, working together, or a solution
                everyone read in the same place — read both before deciding there is anything to
                raise.
              </p>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Side({ side }: { side: ArtifactSide }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="font-semibold">{side.student_name}</span>
        <Badge variant="outline" className={cn('py-px', verdictTone(side.code.verdict))}>
          {VERDICT_LABEL[side.code.verdict] ?? side.code.verdict}
        </Badge>
        <span className="text-muted-foreground">{side.code.language}</span>
        <span className="ml-auto text-muted-foreground">
          {formatWhenExact(side.code.submitted_at)}
        </span>
      </div>
      <Card className="overflow-hidden p-0 shadow-none">
        <CodeEditor value={side.code.code} language={side.code.language} readOnly height="460px" />
      </Card>
    </div>
  );
}
